<template>
  <div class="preferencias-page">
    <b-row>
      <b-col cols="12" md="8">
        <b-card>
          <template #header>
            <h4 class="mb-0">
              <feather-icon icon="SettingsIcon" class="mr-75" />
              Preferências do Sistema
            </h4>
          </template>

          <b-overlay :show="loading" rounded="sm">
            <p class="text-muted mb-2">
              Ajuste comportamentos opcionais do sistema para este tenant. As alterações se aplicam a todos os usuários.
            </p>

            <hr />

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5>Usar condição de pagamento nos pedidos</h5>
                <p class="text-muted mb-0">Quando desativado, oculta código e descrição em todos os perfis e permite pedidos sem condição de pagamento.</p>
              </div>
              <b-form-checkbox v-model="form.condicaoPagamentoAtiva" switch :disabled="loading || saving"
                @change="salvarCondicaoPagamento">
                {{ form.condicaoPagamentoAtiva ? 'Ativado' : 'Desativado' }}
              </b-form-checkbox>
            </div>
            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="FileTextIcon" class="mr-50" />
                  Rascunho de Pedidos
                </h5>
                <p class="text-muted mb-0">
                  Permite salvar pedidos como rascunho para edição posterior antes do envio ao Datasul. Quando desativado,
                  pedidos só podem ser finalizados e enviados diretamente.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.rascunhoAtivo"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleRascunho"
              >
                <span class="ml-1">{{ form.rascunhoAtivo ? 'Ativo' : 'Inativo' }}</span>
              </b-form-checkbox>
            </div>

            <div v-if="form.rascunhoAtivo" class="pref-item pref-subitem py-2 pl-2 border-left">
              <div class="d-flex align-items-start justify-content-between">
                <div class="pref-info pr-2">
                  <h6 class="mb-1">Rascunho apenas para pedidos de doação</h6>
                  <p class="text-muted mb-0 small">
                    Quando ativo, somente pedidos de doação (WD) podem ser salvos como rascunho (pré-pedido).
                  </p>
                </div>
                <b-form-checkbox
                  v-model="form.rascunhoApenasDoacao"
                  switch
                  :disabled="saving"
                  @change="onToggleRascunhoApenasDoacao"
                >
                  <span class="ml-1">{{ form.rascunhoApenasDoacao ? 'Apenas doação' : 'Todos os tipos' }}</span>
                </b-form-checkbox>
              </div>
              <div class="mt-1">
                <h6 class="mb-50">Perfis que podem usar rascunho</h6>
                <p class="text-muted mb-50 small">
                  Se nenhum perfil for selecionado, todos os perfis podem usar rascunho.
                </p>
                <b-spinner v-if="loadingPerfis" small />
                <div v-else class="d-flex flex-wrap">
                  <b-form-checkbox
                    v-for="perfil in perfisDisponiveis"
                    :key="perfil.id"
                    v-model="form.rascunhoPerfis"
                    :value="perfil.perfil_acesso_nome"
                    :disabled="saving"
                    class="mr-2 mb-50"
                    @change="onChangeRascunhoPerfis"
                  >
                    {{ perfil.perfil_acesso_nome }}
                  </b-form-checkbox>
                </div>
              </div>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="PackageIcon" class="mr-50" />
                  Controle de Estoque
                </h5>
                <p class="text-muted mb-0">
                  Exibe informações de estoque (saldo TOTVS, badges "Sem estoque", "Esgotado") em catálogos e na adição de
                  itens ao pedido. Quando desativado, a empresa opera sem mostrar disponibilidade de estoque ao usuário.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.controleEstoqueAtivo"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleEstoque"
              >
                <span class="ml-1">{{ form.controleEstoqueAtivo ? 'Ativo' : 'Inativo' }}</span>
              </b-form-checkbox>
            </div>

            <div
              class="pref-item pref-sub d-flex align-items-start justify-content-between py-2"
              :class="{ 'pref-disabled': !form.controleEstoqueAtivo }"
            >
              <div class="pref-info pr-2 pl-3">
                <h5 class="mb-1">
                  <feather-icon icon="LockIcon" class="mr-50" />
                  Travar venda sem estoque
                </h5>
                <p class="text-muted mb-0">
                  Quando ativo, bloqueia adicionar item ao pedido se a quantidade desejada ultrapassar o estoque disponível
                  (TOTVS). Desativado, o sistema apenas exibe a informação e permite seguir com a venda.
                  <small v-if="!form.controleEstoqueAtivo" class="d-block mt-50 text-warning">
                    <feather-icon icon="AlertTriangleIcon" size="12" /> Requer "Controle de Estoque" ativo.
                  </small>
                </p>
              </div>
              <b-form-checkbox
                v-model="form.bloquearVendaSemEstoque"
                switch
                size="lg"
                :disabled="saving || !form.controleEstoqueAtivo"
                @change="onToggleBloquearEstoque"
              >
                <span class="ml-1">{{ form.bloquearVendaSemEstoque ? 'Ativo' : 'Inativo' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="AwardIcon" class="mr-50" />
                  Comissões
                </h5>
                <p class="text-muted mb-0">
                  Exibe cards de comissão no dashboard, gráficos (comissão por mês, notas de comissão) e o módulo de
                  Relatórios de Comissões. Quando desativado, todos os recursos relacionados a comissão ficam ocultos para
                  todos os usuários da empresa.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.comissoesAtivo"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleComissoes"
              >
                <span class="ml-1">{{ form.comissoesAtivo ? 'Ativo' : 'Inativo' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="ShieldIcon" class="mr-50" />
                  Bloquear estouro de budget (doações)
                </h5>
                <p class="text-muted mb-0">
                  Quando ativo, impede finalizar uma doação (pedido WD) que ultrapasse o saldo do budget. Quando
                  desativado, a doação é permitida mesmo sem saldo — o sistema apenas avisa e o saldo fica negativo,
                  refletindo o estouro.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.bloquearEstouroBudget"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleBloquearEstouro"
              >
                <span class="ml-1">{{ form.bloquearEstouroBudget ? 'Bloquear' : 'Alertar' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="RotateCcwIcon" class="mr-50" />
                  Solicitações de Devolução
                </h5>
                <p class="text-muted mb-0">
                  Exibe o submódulo "Solicitações" em Devoluções e Trocas, onde o usuário registra e acompanha pedidos de
                  devolução/troca. Quando desativado, o item de menu fica oculto para todos os usuários da empresa
                  (o "Saldo de Devolução" continua disponível).
                </p>
              </div>
              <b-form-checkbox
                v-model="form.solicitacaoDevolucaoAtivo"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleSolicitacaoDevolucao"
              >
                <span class="ml-1">{{ form.solicitacaoDevolucaoAtivo ? 'Ativo' : 'Inativo' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="UserPlusIcon" class="mr-50" />
                  Cadastro de Cliente
                </h5>
                <p class="text-muted mb-0">
                  Exibe o botão "Novo (Form)" na listagem de Clientes, permitindo cadastrar um cliente por formulário.
                  Quando desativado, o botão fica oculto para todos os usuários da empresa.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.cadastroClienteAtivo"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleCadastroCliente"
              >
                <span class="ml-1">{{ form.cadastroClienteAtivo ? 'Ativo' : 'Inativo' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="EyeOffIcon" class="mr-50" />
                  Ocultar informações comerciais do Cliente
                </h5>
                <p class="text-muted mb-0">
                  Quando ativo, o perfil Cliente não vê as informações de representante, condição de pagamento e
                  tabela de preço (Info. Pedido, carrinho e Operações). Ocultação apenas visual — os cálculos e o
                  envio do pedido continuam iguais. Não afeta os demais perfis.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.clienteOcultarInfoComercial"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleOcultarInfoComercial"
              >
                <span class="ml-1">{{ form.clienteOcultarInfoComercial ? 'Ocultar' : 'Exibir' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="EyeOffIcon" class="mr-50" />
                  Ocultar tabela de preço e condição de pagamento do Representante
                </h5>
                <p class="text-muted mb-0">
                  Quando ativo, o perfil Representante não vê a tabela de preço e a condição de pagamento
                  (Info. Pedido, carrinho e Operações). Ocultação apenas visual — os cálculos e o envio do
                  pedido continuam iguais. Não afeta os demais perfis.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.representanteOcultarInfoComercial"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleOcultarInfoComercialRep"
              >
                <span class="ml-1">{{ form.representanteOcultarInfoComercial ? 'Ocultar' : 'Exibir' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="EyeOffIcon" class="mr-50" />
                  Ocultar tabela de preço e condição de pagamento do Supervisor
                </h5>
                <p class="text-muted mb-0">
                  Quando ativo, o perfil Supervisor não vê a tabela de preço e a condição de pagamento
                  (Info. Pedido, carrinho e Operações). Ocultação apenas visual — os cálculos e o envio do
                  pedido continuam iguais. Não afeta os demais perfis.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.supervisorOcultarInfoComercial"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleOcultarInfoComercialSup"
              >
                <span class="ml-1">{{ form.supervisorOcultarInfoComercial ? 'Ocultar' : 'Exibir' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="TagIcon" class="mr-50" />
                  Funcionalidade PIC
                </h5>
                <p class="text-muted mb-0">
                  Quando ativo, exibe os menus e telas do PIC (Painel PIC, PIC, Tipo PIC e PIC Regra) e a
                  seleção de PIC nos pedidos. Quando inativo (padrão), tudo relacionado a PIC fica oculto e
                  as rotas do PIC ficam bloqueadas no servidor.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.picAtivo"
                switch
                size="lg"
                :disabled="saving"
                @change="onTogglePicAtivo"
              >
                <span class="ml-1">{{ form.picAtivo ? 'Ativado' : 'Desativado' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="UsersIcon" class="mr-50" />
                  Módulo CRM
                </h5>
                <p class="text-muted mb-0">
                  Exibe o módulo CRM (Clientes, Agenda, Oportunidades, Campanhas, Aniversariantes, Gamificação e
                  Marketing) no menu e libera suas telas. Quando desativado, o CRM some do menu e as rotas ficam
                  bloqueadas no servidor. Ativado por padrão.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.crmAtivo"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleCrmAtivo"
              >
                <span class="ml-1">{{ form.crmAtivo ? 'Ativado' : 'Desativado' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="GiftIcon" class="mr-50" />
                  Representante vê apenas pedidos de doação
                </h5>
                <p class="text-muted mb-0">
                  Quando ativo, o perfil Representante só enxerga pedidos de doação (WD) nas listagens de
                  pedidos, rascunhos e dashboards. Não afeta os demais perfis.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.representanteApenasDoacao"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleRepresentanteApenasDoacao"
              >
                <span class="ml-1">{{ form.representanteApenasDoacao ? 'Apenas doação' : 'Todos os pedidos' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="FileIcon" class="mr-50" />
                  Catálogo em PDF
                </h5>
                <p class="text-muted mb-0">
                  Exibe o botão "Catálogo PDF" na home (Portal B2B) para baixar o catálogo da empresa. Quando ativo,
                  envie o arquivo PDF abaixo. Quando desativado, o botão fica oculto para todos os usuários da empresa.
                </p>
                <div v-if="form.catalogoPdfAtivo" class="catalogo-pdf-uploader mt-1">
                  <div class="d-flex align-items-center flex-wrap">
                    <b-form-file
                      ref="catalogoInput"
                      v-model="catalogoFile"
                      accept="application/pdf,.pdf"
                      placeholder="Escolha um PDF..."
                      drop-placeholder="Solte o PDF aqui..."
                      browse-text="Procurar"
                      :disabled="uploadingCatalogo"
                      style="max-width: 320px;"
                    />
                    <b-button
                      variant="primary"
                      class="ml-1"
                      :disabled="!catalogoFile || uploadingCatalogo"
                      @click="uploadCatalogoPdf"
                    >
                      <b-spinner v-if="uploadingCatalogo" small />
                      <span v-else>Enviar</span>
                    </b-button>
                  </div>
                  <small v-if="catalogoPdfCaminho" class="d-block mt-50">
                    <feather-icon icon="CheckCircleIcon" size="12" class="text-success" />
                    Arquivo atual:
                    <a :href="catalogoPdfCaminho" target="_blank" rel="noopener">ver catálogo</a>
                  </small>
                  <small v-else class="d-block mt-50 text-warning">
                    <feather-icon icon="AlertTriangleIcon" size="12" />
                    Nenhum catálogo enviado ainda. O botão só aparece na home após o envio.
                  </small>
                </div>
              </div>
              <b-form-checkbox
                v-model="form.catalogoPdfAtivo"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleCatalogoPdf"
              >
                <span class="ml-1">{{ form.catalogoPdfAtivo ? 'Ativo' : 'Inativo' }}</span>
              </b-form-checkbox>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="DollarSignIcon" class="mr-50" />
                  Valor mínimo do pedido
                </h5>
                <p class="text-muted mb-0">
                  Define o valor mínimo (R$) exigido para concluir um pedido. Quando maior que zero, o sistema bloqueia
                  a finalização de pedidos abaixo desse valor. Use 0 para não exigir valor mínimo.
                </p>
              </div>
              <div class="pref-valor-minimo d-flex align-items-center">
                <b-input-group style="width: 170px;">
                  <b-input-group-prepend is-text>
                    R$
                  </b-input-group-prepend>
                  <b-form-input
                    v-model.number="form.valorMinimoPedido"
                    type="number"
                    min="0"
                    step="0.01"
                    :disabled="savingValorMinimo"
                  />
                </b-input-group>
                <b-button
                  variant="primary"
                  class="ml-1"
                  :disabled="savingValorMinimo"
                  @click="salvarValorMinimo"
                >
                  <b-spinner v-if="savingValorMinimo" small />
                  <span v-else>Salvar</span>
                </b-button>
              </div>
            </div>

            <div class="pref-item d-flex align-items-start justify-content-between py-2">
              <div class="pref-info pr-2">
                <h5 class="mb-1">
                  <feather-icon icon="LockIcon" class="mr-50" />
                  Bloquear pedidos abaixo do valor mínimo
                </h5>
                <p class="text-muted mb-0">
                  Quando ativo, pedidos abaixo do valor mínimo são bloqueados (botão de finalizar desabilitado).
                  Quando inativo, a compra é permitida e o usuário recebe apenas um aviso de confirmação ao finalizar.
                </p>
              </div>
              <b-form-checkbox
                v-model="form.bloquearPedidoMinimo"
                switch
                size="lg"
                :disabled="saving"
                @change="onToggleBloquearPedidoMinimo"
              >
                <span class="ml-1">{{ form.bloquearPedidoMinimo ? 'Bloquear' : 'Somente avisar' }}</span>
              </b-form-checkbox>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-2">
              <div>
                <h6 class="mb-50">Exibir data prevista de entrega</h6>
                <p class="text-muted mb-0">Controla o campo na entrada, nos detalhes e nas demais visualizações de pedidos.</p>
              </div>
              <b-form-checkbox v-model="form.exibirDataPrevistaEntrega" switch size="lg"
                :disabled="saving" @change="onToggleDataPrevistaEntrega">
                <span class="ml-1">{{ form.exibirDataPrevistaEntrega ? 'Exibir' : 'Ocultar' }}</span>
              </b-form-checkbox>
            </div>
          </b-overlay>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BFormCheckbox, BOverlay,
  BInputGroup, BInputGroupPrepend, BFormInput, BButton, BSpinner, BFormFile,
} from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import SLSParametrosClient from '@/api/SistemaDKP/sls-parametros.client';
import CatalogoPdfClient from '@/api/SistemaDKP/catalogo-pdf.client';

const PARAM_RASCUNHO = 'RASCUNHO_PEDIDO_ATIVO';
const PARAM_RASCUNHO_APENAS_DOACAO = 'RASCUNHO_APENAS_DOACAO';
const PARAM_RASCUNHO_PERFIS = 'RASCUNHO_PEDIDO_PERFIS';
const PARAM_ESTOQUE = 'CONTROLE_ESTOQUE_ATIVO';
const PARAM_BLOQUEAR_ESTOQUE = 'BLOQUEAR_VENDA_SEM_ESTOQUE';
const PARAM_COMISSOES = 'COMISSOES_ATIVO';
const PARAM_VALOR_MINIMO = 'VALOR_MINIMO_PEDIDO';
const PARAM_BLOQUEAR_ESTOURO_BUDGET = 'BUDGET_BLOQUEAR_ESTOURO';
const PARAM_SOLICITACAO_DEVOLUCAO = 'SOLICITACAO_DEVOLUCAO_ATIVO';
const PARAM_CADASTRO_CLIENTE = 'CADASTRO_CLIENTE_ATIVO';
const PARAM_CATALOGO_PDF_ATIVO = 'CATALOGO_PDF_ATIVO';
const PARAM_CATALOGO_PDF_CAMINHO = 'CATALOGO_PDF_CAMINHO';
const PARAM_BLOQUEAR_PEDIDO_MINIMO = 'BLOQUEAR_PEDIDO_MINIMO';
const PARAM_CLIENTE_OCULTAR_INFO_COMERCIAL = 'CLIENTE_OCULTAR_INFO_COMERCIAL';
const PARAM_REPRESENTANTE_OCULTAR_INFO_COMERCIAL = 'REPRESENTANTE_OCULTAR_INFO_COMERCIAL';
const PARAM_REPRESENTANTE_APENAS_DOACAO = 'REPRESENTANTE_APENAS_DOACAO';
const PARAM_SUPERVISOR_OCULTAR_INFO_COMERCIAL = 'SUPERVISOR_OCULTAR_INFO_COMERCIAL';
const PARAM_PIC_ATIVO = 'PIC_ATIVO';
const PARAM_CRM_ATIVO = 'CRM_ATIVO';

export default {
  name: 'Preferencias',
  components: {
    BRow, BCol, BCard, BFormCheckbox, BOverlay,
    BInputGroup, BInputGroupPrepend, BFormInput, BButton, BSpinner, BFormFile,
  },
  data() {
    return {
      loading: false,
      saving: false,
      savingValorMinimo: false,
      paramIdRascunho: null,
      paramIdRascunhoApenasDoacao: null,
      paramIdRascunhoPerfis: null,
      perfisDisponiveis: [],
      loadingPerfis: false,
      paramIdEstoque: null,
      paramIdBloquearEstoque: null,
      paramIdComissoes: null,
      paramIdValorMinimo: null,
      paramIdBloquearEstouroBudget: null,
      paramIdSolicitacaoDevolucao: null,
      paramIdCadastroCliente: null,
      paramIdCatalogoPdfAtivo: null,
      paramIdCatalogoPdfCaminho: null,
      paramIdBloquearPedidoMinimo: null,
      paramIdOcultarInfoComercial: null,
      paramIdOcultarInfoComercialRep: null,
      paramIdOcultarInfoComercialSup: null,
      paramIdRepresentanteApenasDoacao: null,
      paramIdPicAtivo: null,
      paramIdCrmAtivo: null,
      paramIdCondicaoPagamento: null,
      catalogoPdfCaminho: '',
      catalogoFile: null,
      uploadingCatalogo: false,
      form: {
        exibirDataPrevistaEntrega: true,
        rascunhoAtivo: false,
        rascunhoApenasDoacao: false,
        rascunhoPerfis: [],
        controleEstoqueAtivo: true,
        bloquearVendaSemEstoque: false,
        comissoesAtivo: true,
        valorMinimoPedido: 0,
        bloquearEstouroBudget: true,
        solicitacaoDevolucaoAtivo: true,
        cadastroClienteAtivo: false,
        catalogoPdfAtivo: false,
        bloquearPedidoMinimo: true,
        clienteOcultarInfoComercial: false,
        representanteOcultarInfoComercial: false,
        supervisorOcultarInfoComercial: false,
        representanteApenasDoacao: false,
        picAtivo: false,
        // CRM: ausente = ligado (padrão)
        crmAtivo: true,
        condicaoPagamentoAtiva: true,
      },
      client: new SLSParametrosClient(),
      paramIdDataPrevistaEntrega: null,
      catalogoClient: new CatalogoPdfClient(),
    };
  },
  async mounted() {
    await this.carregar();
    this.$store.dispatch('preferencias/load', { force: true }).catch(() => {});
  },
  methods: {
    async salvarCondicaoPagamento(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdCondicaoPagamento', paramKey: 'CONDICAO_PAGAMENTO_ATIVA', newValue,
        labelLigado: 'Condição de pagamento habilitada.',
        labelDesligado: 'Condição de pagamento oculta e não obrigatória.',
      });
      if (!ok) this.form.condicaoPagamentoAtiva = !newValue;
    },
    isTrue(v) {
      if (v == null) return false;
      const s = String(v).trim().toLowerCase();
      return s === 's' || s === 'sim' || s === 'true' || s === '1' || s === 'y' || s === 'yes';
    },
    async carregar() {
      this.loading = true;
      try {
        const respCondicao = await this.client.findByDescricao('CONDICAO_PAGAMENTO_ATIVA');
        const rowCondicao = respCondicao?.data || respCondicao;
        this.paramIdCondicaoPagamento = rowCondicao?.id || null;
        this.form.condicaoPagamentoAtiva = rowCondicao?.parametro_valor == null || this.isTrue(rowCondicao.parametro_valor);
        const [respR, respRD, respRP, respE, respB, respC, respBE, respSD, respCC] = await Promise.all([
          this.client.findByDescricao(PARAM_RASCUNHO).catch(() => null),
          this.client.findByDescricao(PARAM_RASCUNHO_APENAS_DOACAO).catch(() => null),
          this.client.findByDescricao(PARAM_RASCUNHO_PERFIS).catch(() => null),
          this.client.findByDescricao(PARAM_ESTOQUE).catch(() => null),
          this.client.findByDescricao(PARAM_BLOQUEAR_ESTOQUE).catch(() => null),
          this.client.findByDescricao(PARAM_COMISSOES).catch(() => null),
          this.client.findByDescricao(PARAM_BLOQUEAR_ESTOURO_BUDGET).catch(() => null),
          this.client.findByDescricao(PARAM_SOLICITACAO_DEVOLUCAO).catch(() => null),
          this.client.findByDescricao(PARAM_CADASTRO_CLIENTE).catch(() => null),
        ]);
        const rowR = respR?.data || respR;
        if (rowR && rowR.id) {
          this.paramIdRascunho = rowR.id;
          this.form.rascunhoAtivo = this.isTrue(rowR.parametro_valor);
        } else {
          this.paramIdRascunho = null;
          this.form.rascunhoAtivo = false;
        }
        const rowRD = respRD?.data || respRD;
        if (rowRD && rowRD.id) {
          this.paramIdRascunhoApenasDoacao = rowRD.id;
          this.form.rascunhoApenasDoacao = this.isTrue(rowRD.parametro_valor);
        }
        const rowRP = respRP?.data || respRP;
        if (rowRP && rowRP.id) {
          this.paramIdRascunhoPerfis = rowRP.id;
          this.form.rascunhoPerfis = String(rowRP.parametro_valor || '')
            .split(',').map((v) => v.trim()).filter(Boolean);
        }
        this.carregarPerfis();
        const rowE = respE?.data || respE;
        if (rowE && rowE.id) {
          this.paramIdEstoque = rowE.id;
          this.form.controleEstoqueAtivo = this.isTrue(rowE.parametro_valor);
        } else {
          this.paramIdEstoque = null;
          this.form.controleEstoqueAtivo = true;
        }
        const rowB = respB?.data || respB;
        if (rowB && rowB.id) {
          this.paramIdBloquearEstoque = rowB.id;
          this.form.bloquearVendaSemEstoque = this.isTrue(rowB.parametro_valor);
        } else {
          this.paramIdBloquearEstoque = null;
          this.form.bloquearVendaSemEstoque = false;
        }
        const rowC = respC?.data || respC;
        if (rowC && rowC.id) {
          this.paramIdComissoes = rowC.id;
          this.form.comissoesAtivo = this.isTrue(rowC.parametro_valor);
        } else {
          this.paramIdComissoes = null;
          this.form.comissoesAtivo = true;
        }
        const rowBE = respBE?.data || respBE;
        if (rowBE && rowBE.id) {
          this.paramIdBloquearEstouroBudget = rowBE.id;
          this.form.bloquearEstouroBudget = this.isTrue(rowBE.parametro_valor);
        } else {
          this.paramIdBloquearEstouroBudget = null;
          this.form.bloquearEstouroBudget = true;
        }
        const rowSD = respSD?.data || respSD;
        if (rowSD && rowSD.id) {
          this.paramIdSolicitacaoDevolucao = rowSD.id;
          this.form.solicitacaoDevolucaoAtivo = this.isTrue(rowSD.parametro_valor);
        } else {
          this.paramIdSolicitacaoDevolucao = null;
          this.form.solicitacaoDevolucaoAtivo = true;
        }
        const rowCC = respCC?.data || respCC;
        if (rowCC && rowCC.id) {
          this.paramIdCadastroCliente = rowCC.id;
          this.form.cadastroClienteAtivo = this.isTrue(rowCC.parametro_valor);
        } else {
          this.paramIdCadastroCliente = null;
          this.form.cadastroClienteAtivo = false;
        }
        const respVM = await this.client.findByDescricao(PARAM_VALOR_MINIMO).catch(() => null);
        const rowVM = respVM?.data || respVM;
        if (rowVM && rowVM.id) {
          this.paramIdValorMinimo = rowVM.id;
          const num = Number(String(rowVM.parametro_valor ?? '0').replace(',', '.'));
          this.form.valorMinimoPedido = Number.isNaN(num) ? 0 : num;
        } else {
          this.paramIdValorMinimo = null;
          this.form.valorMinimoPedido = 0;
        }
        const respDataPrevista = await this.client.findByDescricao('EXIBIR_DATA_PREVISTA_ENTREGA', true);
        const rowDataPrevista = respDataPrevista?.data || respDataPrevista;
        this.paramIdDataPrevistaEntrega = rowDataPrevista?.id || null;
        this.form.exibirDataPrevistaEntrega = rowDataPrevista?.parametro_valor == null || this.isTrue(rowDataPrevista.parametro_valor);
        const respBPM = await this.client.findByDescricao(PARAM_BLOQUEAR_PEDIDO_MINIMO).catch(() => null);
        const rowBPM = respBPM?.data || respBPM;
        if (rowBPM && rowBPM.id) {
          this.paramIdBloquearPedidoMinimo = rowBPM.id;
          this.form.bloquearPedidoMinimo = this.isTrue(rowBPM.parametro_valor);
        } else {
          this.paramIdBloquearPedidoMinimo = null;
          // Ausente → comportamento histórico: bloquear
          this.form.bloquearPedidoMinimo = true;
        }
        const respOIC = await this.client.findByDescricao(PARAM_CLIENTE_OCULTAR_INFO_COMERCIAL).catch(() => null);
        const rowOIC = respOIC?.data || respOIC;
        if (rowOIC && rowOIC.id) {
          this.paramIdOcultarInfoComercial = rowOIC.id;
          this.form.clienteOcultarInfoComercial = this.isTrue(rowOIC.parametro_valor);
        } else {
          this.paramIdOcultarInfoComercial = null;
          // Ausente → comportamento histórico: exibir
          this.form.clienteOcultarInfoComercial = false;
        }
        const [respOICR, respRAD, respOICS, respPic, respCrm] = await Promise.all([
          this.client.findByDescricao(PARAM_REPRESENTANTE_OCULTAR_INFO_COMERCIAL).catch(() => null),
          this.client.findByDescricao(PARAM_REPRESENTANTE_APENAS_DOACAO).catch(() => null),
          this.client.findByDescricao(PARAM_SUPERVISOR_OCULTAR_INFO_COMERCIAL).catch(() => null),
          this.client.findByDescricao(PARAM_PIC_ATIVO).catch(() => null),
          this.client.findByDescricao(PARAM_CRM_ATIVO).catch(() => null),
        ]);
        const rowOICS = respOICS?.data || respOICS;
        if (rowOICS && rowOICS.id) {
          this.paramIdOcultarInfoComercialSup = rowOICS.id;
          this.form.supervisorOcultarInfoComercial = this.isTrue(rowOICS.parametro_valor);
        } else {
          this.paramIdOcultarInfoComercialSup = null;
          // Ausente → comportamento histórico: exibir
          this.form.supervisorOcultarInfoComercial = false;
        }
        const rowPicAtv = respPic?.data || respPic;
        if (rowPicAtv && rowPicAtv.id) {
          this.paramIdPicAtivo = rowPicAtv.id;
          this.form.picAtivo = this.isTrue(rowPicAtv.parametro_valor);
        } else {
          this.paramIdPicAtivo = null;
          // Ausente → PIC desligado (padrão)
          this.form.picAtivo = false;
        }
        const rowCrmAtv = respCrm?.data || respCrm;
        if (rowCrmAtv && rowCrmAtv.id) {
          this.paramIdCrmAtivo = rowCrmAtv.id;
          this.form.crmAtivo = this.isTrue(rowCrmAtv.parametro_valor);
        } else {
          this.paramIdCrmAtivo = null;
          // Ausente → CRM ligado (padrão)
          this.form.crmAtivo = true;
        }
        const rowOICR = respOICR?.data || respOICR;
        if (rowOICR && rowOICR.id) {
          this.paramIdOcultarInfoComercialRep = rowOICR.id;
          this.form.representanteOcultarInfoComercial = this.isTrue(rowOICR.parametro_valor);
        } else {
          this.paramIdOcultarInfoComercialRep = null;
          // Ausente → comportamento histórico: exibir
          this.form.representanteOcultarInfoComercial = false;
        }
        const rowRAD = respRAD?.data || respRAD;
        if (rowRAD && rowRAD.id) {
          this.paramIdRepresentanteApenasDoacao = rowRAD.id;
          this.form.representanteApenasDoacao = this.isTrue(rowRAD.parametro_valor);
        } else {
          this.paramIdRepresentanteApenasDoacao = null;
          // Ausente → comportamento histórico: representante vê todos os pedidos
          this.form.representanteApenasDoacao = false;
        }
        const [respCPA, respCPC] = await Promise.all([
          this.client.findByDescricao(PARAM_CATALOGO_PDF_ATIVO).catch(() => null),
          this.client.findByDescricao(PARAM_CATALOGO_PDF_CAMINHO).catch(() => null),
        ]);
        const rowCPA = respCPA?.data || respCPA;
        if (rowCPA && rowCPA.id) {
          this.paramIdCatalogoPdfAtivo = rowCPA.id;
          this.form.catalogoPdfAtivo = this.isTrue(rowCPA.parametro_valor);
        } else {
          this.paramIdCatalogoPdfAtivo = null;
          this.form.catalogoPdfAtivo = false;
        }
        const rowCPC = respCPC?.data || respCPC;
        if (rowCPC && rowCPC.id) {
          this.paramIdCatalogoPdfCaminho = rowCPC.id;
          this.catalogoPdfCaminho = rowCPC.parametro_valor || '';
        } else {
          this.paramIdCatalogoPdfCaminho = null;
          this.catalogoPdfCaminho = '';
        }
      } catch (e) {
        this.paramIdRascunho = null;
        this.paramIdEstoque = null;
        this.paramIdBloquearEstoque = null;
        this.paramIdComissoes = null;
        this.paramIdValorMinimo = null;
        this.paramIdBloquearEstouroBudget = null;
        this.paramIdSolicitacaoDevolucao = null;
        this.paramIdCadastroCliente = null;
        this.paramIdBloquearPedidoMinimo = null;
        this.paramIdOcultarInfoComercial = null;
        this.paramIdOcultarInfoComercialRep = null;
        this.paramIdOcultarInfoComercialSup = null;
        this.paramIdRepresentanteApenasDoacao = null;
        this.paramIdPicAtivo = null;
        this.paramIdCrmAtivo = null;
      } finally {
        this.loading = false;
      }
    },
    async salvarValorMinimo() {
      this.savingValorMinimo = true;
      const num = Number(this.form.valorMinimoPedido);
      const valor = Number.isNaN(num) || num < 0 ? '0' : String(num);
      try {
        if (this.paramIdValorMinimo) {
          await this.client.update(this.paramIdValorMinimo, {
            parametro_descricao: PARAM_VALOR_MINIMO,
            parametro_valor: valor,
          });
        } else {
          const created = await this.client.create({
            parametro_descricao: PARAM_VALOR_MINIMO,
            parametro_valor: valor,
          });
          this.paramIdValorMinimo = created?.data?.id || created?.id || null;
        }
        this.form.valorMinimoPedido = Number(valor);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Preferência atualizada',
            text: Number(valor) > 0
              ? `Valor mínimo do pedido definido para R$ ${Number(valor).toFixed(2)}.`
              : 'Valor mínimo do pedido removido (sem exigência).',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar',
            text: e.message || 'Não foi possível atualizar o valor mínimo do pedido.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.savingValorMinimo = false;
      }
    },
    async salvarParam({ idField, paramKey, newValue, labelLigado, labelDesligado }) {
      this.saving = true;
      const valor = newValue ? 'S' : 'N';
      try {
        if (this[idField]) {
          await this.client.update(this[idField], {
            parametro_descricao: paramKey,
            parametro_valor: valor,
          });
        } else {
          const created = await this.client.create({
            parametro_descricao: paramKey,
            parametro_valor: valor,
          });
          this[idField] = created?.data?.id || created?.id || null;
        }
        await this.$store.dispatch('preferencias/load', { force: true });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Preferência atualizada',
            text: newValue ? labelLigado : labelDesligado,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        return true;
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar',
            text: e.message || 'Não foi possível atualizar a preferência.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
        return false;
      } finally {
        this.saving = false;
      }
    },
    async onToggleBloquearPedidoMinimo(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdBloquearPedidoMinimo',
        paramKey: PARAM_BLOQUEAR_PEDIDO_MINIMO,
        newValue,
        labelLigado: 'Pedidos abaixo do valor mínimo agora serão bloqueados.',
        labelDesligado: 'Pedidos abaixo do valor mínimo serão apenas avisados (não bloqueados).',
      });
      if (!ok) this.form.bloquearPedidoMinimo = !newValue;
    },
    async onToggleDataPrevistaEntrega(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdDataPrevistaEntrega',
        paramKey: 'EXIBIR_DATA_PREVISTA_ENTREGA',
        newValue,
        labelLigado: 'Data prevista de entrega visível.',
        labelDesligado: 'Data prevista de entrega oculta.',
      });
      if (!ok) this.form.exibirDataPrevistaEntrega = !newValue;
    },
    async onToggleOcultarInfoComercial(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdOcultarInfoComercial',
        paramKey: PARAM_CLIENTE_OCULTAR_INFO_COMERCIAL,
        newValue,
        labelLigado: 'Informações comerciais ocultas para o perfil Cliente.',
        labelDesligado: 'Informações comerciais visíveis para o perfil Cliente.',
      });
      if (!ok) this.form.clienteOcultarInfoComercial = !newValue;
    },
    async onToggleOcultarInfoComercialRep(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdOcultarInfoComercialRep',
        paramKey: PARAM_REPRESENTANTE_OCULTAR_INFO_COMERCIAL,
        newValue,
        labelLigado: 'Tabela de preço e condição de pagamento ocultas para o perfil Representante.',
        labelDesligado: 'Tabela de preço e condição de pagamento visíveis para o perfil Representante.',
      });
      if (!ok) this.form.representanteOcultarInfoComercial = !newValue;
    },
    async onToggleOcultarInfoComercialSup(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdOcultarInfoComercialSup',
        paramKey: PARAM_SUPERVISOR_OCULTAR_INFO_COMERCIAL,
        newValue,
        labelLigado: 'Tabela de preço e condição de pagamento ocultas para o perfil Supervisor.',
        labelDesligado: 'Tabela de preço e condição de pagamento visíveis para o perfil Supervisor.',
      });
      if (!ok) this.form.supervisorOcultarInfoComercial = !newValue;
    },
    async onTogglePicAtivo(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdPicAtivo',
        paramKey: PARAM_PIC_ATIVO,
        newValue,
        labelLigado: 'Funcionalidade PIC ativada (menus e telas visíveis).',
        labelDesligado: 'Funcionalidade PIC desativada (menus e telas ocultos).',
      });
      if (!ok) this.form.picAtivo = !newValue;
    },
    async onToggleCrmAtivo(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdCrmAtivo',
        paramKey: PARAM_CRM_ATIVO,
        newValue,
        labelLigado: 'Módulo CRM ativado (menu e telas visíveis).',
        labelDesligado: 'Módulo CRM desativado (menu oculto e rotas bloqueadas).',
      });
      if (!ok) this.form.crmAtivo = !newValue;
    },
    async onToggleRepresentanteApenasDoacao(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdRepresentanteApenasDoacao',
        paramKey: PARAM_REPRESENTANTE_APENAS_DOACAO,
        newValue,
        labelLigado: 'O perfil Representante agora vê apenas pedidos de doação (WD).',
        labelDesligado: 'O perfil Representante voltou a ver todos os pedidos.',
      });
      if (!ok) this.form.representanteApenasDoacao = !newValue;
    },
    async carregarPerfis() {
      this.loadingPerfis = true;
      try {
        const resp = await this.$http.get('/perfilAcesso/findAll', { params: { perPage: 1000 } });
        const lista = resp?.data?.data || resp?.data || [];
        this.perfisDisponiveis = (Array.isArray(lista) ? lista : [])
          .filter((p) => p && p.perfil_acesso_nome)
          .sort((a, b) => String(a.perfil_acesso_nome).localeCompare(String(b.perfil_acesso_nome)));
        // Normaliza seleção salva para os nomes reais (case-insensitive).
        const nomes = this.perfisDisponiveis.map((p) => p.perfil_acesso_nome);
        this.form.rascunhoPerfis = this.form.rascunhoPerfis
          .map((sel) => nomes.find((n) => n.toLowerCase() === String(sel).toLowerCase()))
          .filter(Boolean);
      } catch (e) {
        this.perfisDisponiveis = [];
      } finally {
        this.loadingPerfis = false;
      }
    },
    // Salva um parâmetro com valor livre (não S/N).
    async salvarParamValor({ idField, paramKey, valor, mensagem }) {
      this.saving = true;
      try {
        if (this[idField]) {
          await this.client.update(this[idField], {
            parametro_descricao: paramKey,
            parametro_valor: valor,
          });
        } else {
          const created = await this.client.create({
            parametro_descricao: paramKey,
            parametro_valor: valor,
          });
          this[idField] = created?.data?.id || created?.id || null;
        }
        await this.$store.dispatch('preferencias/load', { force: true });
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Preferência atualizada', text: mensagem, icon: 'CheckCircleIcon', variant: 'success' },
        });
        return true;
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Erro ao salvar', text: e.message || 'Não foi possível atualizar a preferência.', icon: 'AlertCircleIcon', variant: 'danger' },
        });
        return false;
      } finally {
        this.saving = false;
      }
    },
    async onToggleRascunhoApenasDoacao(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdRascunhoApenasDoacao',
        paramKey: PARAM_RASCUNHO_APENAS_DOACAO,
        newValue,
        labelLigado: 'Rascunho permitido apenas para pedidos de doação.',
        labelDesligado: 'Rascunho permitido para todos os tipos de pedido.',
      });
      if (!ok) this.form.rascunhoApenasDoacao = !newValue;
    },
    async onChangeRascunhoPerfis() {
      await this.salvarParamValor({
        idField: 'paramIdRascunhoPerfis',
        paramKey: PARAM_RASCUNHO_PERFIS,
        valor: this.form.rascunhoPerfis.join(','),
        mensagem: this.form.rascunhoPerfis.length
          ? `Rascunho restrito aos perfis: ${this.form.rascunhoPerfis.join(', ')}.`
          : 'Rascunho liberado para todos os perfis.',
      });
    },
    async onToggleRascunho(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdRascunho',
        paramKey: PARAM_RASCUNHO,
        newValue,
        labelLigado: 'Rascunho de pedidos ativado.',
        labelDesligado: 'Rascunho de pedidos desativado.',
      });
      if (!ok) this.form.rascunhoAtivo = !newValue;
    },
    async onToggleEstoque(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdEstoque',
        paramKey: PARAM_ESTOQUE,
        newValue,
        labelLigado: 'Controle de estoque ativado.',
        labelDesligado: 'Controle de estoque desativado.',
      });
      if (!ok) this.form.controleEstoqueAtivo = !newValue;
    },
    async onToggleBloquearEstoque(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdBloquearEstoque',
        paramKey: PARAM_BLOQUEAR_ESTOQUE,
        newValue,
        labelLigado: 'Venda sem estoque agora será bloqueada.',
        labelDesligado: 'Venda sem estoque será apenas avisada (não bloqueada).',
      });
      if (!ok) this.form.bloquearVendaSemEstoque = !newValue;
    },
    async onToggleComissoes(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdComissoes',
        paramKey: PARAM_COMISSOES,
        newValue,
        labelLigado: 'Comissões ativadas.',
        labelDesligado: 'Comissões desativadas em toda a empresa.',
      });
      if (!ok) this.form.comissoesAtivo = !newValue;
    },
    async onToggleBloquearEstouro(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdBloquearEstouroBudget',
        paramKey: PARAM_BLOQUEAR_ESTOURO_BUDGET,
        newValue,
        labelLigado: 'Doações que estouram o budget serão bloqueadas.',
        labelDesligado: 'Doações que estouram o budget serão apenas avisadas (saldo pode ficar negativo).',
      });
      if (!ok) this.form.bloquearEstouroBudget = !newValue;
    },
    async onToggleSolicitacaoDevolucao(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdSolicitacaoDevolucao',
        paramKey: PARAM_SOLICITACAO_DEVOLUCAO,
        newValue,
        labelLigado: 'Solicitações de devolução ativadas (item de menu visível).',
        labelDesligado: 'Solicitações de devolução desativadas (item de menu oculto).',
      });
      if (!ok) this.form.solicitacaoDevolucaoAtivo = !newValue;
    },
    async onToggleCadastroCliente(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdCadastroCliente',
        paramKey: PARAM_CADASTRO_CLIENTE,
        newValue,
        labelLigado: 'Cadastro de cliente ativado (botão "Novo (Form)" visível).',
        labelDesligado: 'Cadastro de cliente desativado (botão "Novo (Form)" oculto).',
      });
      if (!ok) this.form.cadastroClienteAtivo = !newValue;
    },
    async onToggleCatalogoPdf(newValue) {
      const ok = await this.salvarParam({
        idField: 'paramIdCatalogoPdfAtivo',
        paramKey: PARAM_CATALOGO_PDF_ATIVO,
        newValue,
        labelLigado: 'Catálogo em PDF ativado (botão visível na home).',
        labelDesligado: 'Catálogo em PDF desativado (botão oculto na home).',
      });
      if (!ok) this.form.catalogoPdfAtivo = !newValue;
    },
    async uploadCatalogoPdf() {
      if (!this.catalogoFile) return;
      if (this.catalogoFile.type !== 'application/pdf') {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Arquivo inválido',
            text: 'Selecione um arquivo PDF.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
        return;
      }
      this.uploadingCatalogo = true;
      try {
        const resp = await this.catalogoClient.upload(this.catalogoFile);
        this.catalogoPdfCaminho = resp?.caminho || resp?.data?.caminho || '';
        // O upload faz upsert do parâmetro no backend; recarrega o id para edições futuras.
        const r = await this.client.findByDescricao(PARAM_CATALOGO_PDF_CAMINHO).catch(() => null);
        const row = r?.data || r;
        if (row && row.id) this.paramIdCatalogoPdfCaminho = row.id;
        this.catalogoFile = null;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Catálogo atualizado',
            text: 'O catálogo em PDF foi enviado com sucesso.',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao enviar',
            text: e.message || 'Não foi possível enviar o catálogo em PDF.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.uploadingCatalogo = false;
      }
    },
  },
};
</script>

<style scoped>
.preferencias-page .pref-item {
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.preferencias-page .pref-item:last-child {
  border-bottom: 0;
}
.preferencias-page .pref-info h5 {
  font-weight: 600;
}
</style>
